import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useRealtimeEvent } from '../contexts/RealtimeContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Ticket, Plus, Search, AlertTriangle, Clock, CheckCircle, XCircle, ArrowRight, Image as ImageIcon, Eye, List, Grid3X3, UserCog, RotateCcw, Package, Pause, Shield, Play, Trash2, Camera, RefreshCw, CalendarDays } from 'lucide-react';
import { DataTable } from '../components/ui/data-table';
import { Combobox } from '../components/ui/combobox';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileTicketCard from '../components/tickets/MobileTicketCard';

const TicketsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const { plants, locations } = useData();
  const isMobile = useIsMobile();

  // ── Paginated data ─────────────────────────────────────────────────────────
  const [pageData, setPageData] = useState({
    items: [], total: 0, page: 1, pages: 1, page_size: 50,
    tab_counts: { active: 0, closed: 0, all: 0 },
    period_counts: { today: 0, yesterday: 0, this_week: 0, this_month: 0, last_month: 0, all: 0 },
    kpi_counts: { urgent: 0, in_progress: 0, unassigned: 0, waiting_parts: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // ── Filters ────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [timePeriod, setTimePeriod] = useState('all');
  const searchDebounceRef = useRef(null);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeView, setActiveView] = useState('table');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [localSymptoms, setLocalSymptoms] = useState([]);
  const [localCauses, setLocalCauses] = useState([]);
  const [localInterventionTypes, setLocalInterventionTypes] = useState([]);

  // Equipment autocomplete state
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [showEquipmentSuggestions, setShowEquipmentSuggestions] = useState(false);
  const equipmentSearchRef = useRef(null);
  
  const handleSelectionChange = useCallback((items) => {
    setSelectedTickets(items);
  }, []);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    equipment_id: '',
    reporter_name: '',
    reporter_phone: '',
    reporter_department: '',
    plant_id: '',
    location_id: '',
    priority_requested: 'medium',
    symptom_id: '',
    cause_id: '',
    intervention_type_id: '',
    images: []
  });
  const cameraInputRef = useRef(null);

  // Auto-fill reporter_name from logged-in user
  useEffect(() => {
    if (user?.full_name) {
      setFormData(prev => ({ ...prev, reporter_name: prev.reporter_name || user.full_name }));
    }
  }, [user]);
  
  // ── Core fetch (server-side paginated) ────────────────────────────────────
  const fetchPage = useCallback(async (opts = {}) => {
    const {
      pg = page,
      tab = activeTab,
      period = timePeriod,
      search = searchTerm,
      silent = false,
    } = opts;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pg,
        page_size: PAGE_SIZE,
        status_group: tab,
        time_period: period,
      });
      if (search && search.length >= 2) params.append('search', search);
      const res = await api.get(`/tickets/page?${params}`);
      setPageData(res.data);
    } catch {
      toast.error('Eroare la incarcarea tichetelor');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, activeTab, timePeriod, searchTerm]);

  // Backward compat alias used by WebSocket handlers
  const ticketCountRef = useRef(0);
  const fetchTicketsQuiet = useCallback(async () => {
    await fetchPage({ silent: true });
  }, [fetchPage]);

  const [newTicketAlert, setNewTicketAlert] = useState(null);

  // Real-time: auto-refresh when new ticket arrives
  useRealtimeEvent('new_ticket', useCallback((msg) => {
    const ticketData = msg?.data || {};
    setNewTicketAlert(prev => ({
      count: (prev?.count || 0) + 1,
      timestamp: Date.now(),
      lastTitle: ticketData.title || 'Tichet nou',
    }));
    fetchTicketsQuiet();
  }, [fetchTicketsQuiet]));

  useRealtimeEvent('ticket_update', useCallback(() => {
    fetchTicketsQuiet();
  }, [fetchTicketsQuiet]));

  const handleDismissNewBanner = () => setNewTicketAlert(null);

  // Initial load
  useEffect(() => {
    fetchAllEquipment();
    api.get('/symptoms').then(r => setLocalSymptoms(r.data)).catch(() => {});
    api.get('/causes').then(r => setLocalCauses(r.data)).catch(() => {});
    api.get('/intervention-types').then(r => setLocalInterventionTypes(r.data)).catch(() => {});
  }, []);

  // Refetch on filter or page change
  useEffect(() => {
    fetchPage({ pg: page, tab: activeTab, period: timePeriod, search: searchTerm });
  }, [page, activeTab, timePeriod]); // eslint-disable-line

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage({ pg: 1, tab: activeTab, period: timePeriod, search: value });
    }, 350);
  };

  // Filter change helpers — reset to page 1
  const handleTabChange = (tab) => { setActiveTab(tab); setPage(1); };
  const handlePeriodChange = (p) => { setTimePeriod(p); setPage(1); };

  // Convenience alias
  const fetchTickets = () => fetchPage({ pg: 1 });

  const tickets = pageData.items; // for form/dialog code that still uses `tickets`

  const fetchAllEquipment = async () => {
    try {
      const response = await api.get('/equipment');
      setAllEquipment(response.data);
    } catch (error) { /* ignore */ }
  };

  // Equipment autocomplete - select equipment and auto-fill plant/location
  const handleEquipmentSelect = (eq) => {
    setFormData(prev => ({
      ...prev,
      equipment_id: eq.id,
      plant_id: eq.plant_id || '',
      location_id: eq.location_id || ''
    }));
    setEquipmentSearch(`${eq.name}${eq.code ? ` (${eq.code})` : ''}`);
    setShowEquipmentSuggestions(false);
  };

  const filteredEquipmentSuggestions = equipmentSearch.length >= 2
    ? allEquipment.filter(e =>
        e.name?.toLowerCase().includes(equipmentSearch.toLowerCase()) ||
        e.code?.toLowerCase().includes(equipmentSearch.toLowerCase())
      ).slice(0, 10)
    : [];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/tickets', formData);
      const ticketId = response.data.id;
      
      for (const img of imagePreview) {
        const formDataImg = new FormData();
        formDataImg.append('image', img.file);
        await api.post(`/tickets/${ticketId}/images`, formDataImg, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Tichet creat cu succes!');
      setShowAddDialog(false);
      resetForm();
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la creare');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      equipment_id: '',
      reporter_name: user?.full_name || '',
      reporter_phone: '',
      reporter_department: '',
      plant_id: '',
      location_id: '',
      priority_requested: 'medium',
      symptom_id: '',
      cause_id: '',
      intervention_type_id: '',
      images: []
    });
    setImagePreview([]);
    setEquipmentSearch('');
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-700',
      accepted: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      waiting_parts: 'bg-orange-100 text-orange-700',
      monitoring: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-700',
      rejected: 'bg-red-100 text-red-700',
      transformed: 'bg-cyan-100 text-cyan-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Deschis',
      accepted: 'Acceptat',
      in_progress: 'In Lucru',
      waiting_parts: 'Asteapta Piese',
      monitoring: 'Sub Monitorizare',
      resolved: 'Rezolvat',
      closed: 'Inchis',
      rejected: 'Respins',
      transformed: 'Transformat'
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-500 text-white',
      urgent: 'bg-orange-500 text-white',
      high: 'bg-amber-500 text-white',
      medium: 'bg-yellow-500 text-slate-900',
      low: 'bg-green-500 text-white'
    };
    return colors[priority] || 'bg-slate-500 text-white';
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      critical: 'Critic',
      urgent: 'Urgent',
      high: 'Ridicat',
      medium: 'Mediu',
      low: 'Scazut'
    };
    return labels[priority] || priority;
  };

  const getSlaIcon = (slaStatus) => {
    switch (slaStatus) {
      case 'ok': return <Clock className="w-4 h-4 text-green-500" />;
      case 'at_risk': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'breached': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  // ── Derived from server data ───────────────────────────────────────────────
  const PRIORITY_WEIGHT = { critical: 0, urgent: 1, high: 2, medium: 3, low: 4 };
  // Server already returns sorted items; just use them directly
  const sortedTickets = pageData.items;
  const kpiCounts = {
    urgent:       pageData.kpi_counts?.urgent        ?? 0,
    inProgress:   pageData.kpi_counts?.in_progress   ?? 0,
    unassigned:   pageData.kpi_counts?.unassigned     ?? 0,
    waitingParts: pageData.kpi_counts?.waiting_parts  ?? 0,
    total:        pageData.tab_counts?.active         ?? 0,
    closed:       pageData.tab_counts?.closed         ?? 0,
  };

  // Get plant/location names for display in create form
  // Equipment items enriched with description for Combobox
  const equipmentItems = allEquipment.map(eq => ({
    ...eq,
    name: `${eq.name}${eq.code ? ` (${eq.code})` : ''}`,
    description: [
      plants.find(p => p.id === eq.plant_id)?.name,
      locations.find(l => l.id === eq.location_id)?.name
    ].filter(Boolean).join(' / ')
  }));

  const selectedPlantName = plants.find(p => p.id === formData.plant_id)?.name || '';
  const selectedLocationName = locations.find(l => l.id === formData.location_id)?.name || '';

  const ticketsData = sortedTickets.map(t => ({
    ...t,
    ticket_number: (t.id || '').slice(0, 8).toUpperCase(),
    status_display: getStatusLabel(t.status),
    priority_display: getPriorityLabel(t.priority_assigned || t.priority_requested),
    created_display: new Date(t.created_at).toLocaleDateString('ro-RO'),
    created_time: new Date(t.created_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }),
    closed_time: t.closed_at ? new Date(t.closed_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : null,
    closed_display: t.closed_at ? new Date(t.closed_at).toLocaleDateString('ro-RO') : null,
    has_images: t.images?.length > 0 ? 'Da' : 'Nu'
  }));

  const tableColumns = [
    {
      key: 'ticket_number',
      label: 'Nr.',
      sortable: true,
      filterable: true,
      width: 'w-24',
      render: (value) => (
        <span className="font-mono text-xs text-slate-600">{value}</span>
      )
    },
    {
      key: 'title',
      label: 'Titlu',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-slate-900">{value}</p>
          <p className="text-xs text-slate-500 truncate max-w-[200px]">{row.description}</p>
        </div>
      )
    },
    {
      key: 'equipment_name',
      label: 'Echipament',
      sortable: true,
      filterable: true
    },
    {
      key: 'plant_name',
      label: 'Sediu',
      sortable: true,
      filterable: true
    },
    {
      key: 'location_name',
      label: 'Locatie',
      sortable: true,
      filterable: true
    },
    {
      key: 'status_display',
      label: 'Status',
      sortable: true,
      filterable: true,
      width: 'w-28',
      render: (value, row) => (
        <div className="flex flex-col gap-0.5">
          <Badge className={getStatusColor(row.status)}>{value}</Badge>
          {row.status === 'waiting_parts' && row.parts_eta_days && (
            <span className="text-[10px] text-orange-700 font-medium" data-testid={`ticket-eta-${row.id}`}>
              ETA: {row.parts_eta_days}z{row.parts_eta_date ? ` (${new Date(row.parts_eta_date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })})` : ''}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'priority_display',
      label: 'Prioritate',
      sortable: true,
      filterable: true,
      width: 'w-24',
      render: (value, row) => (
        <Badge className={getPriorityColor(row.priority_assigned || row.priority_requested)}>{value}</Badge>
      )
    },
    {
      key: 'assigned_to_name',
      label: 'Asignat',
      sortable: true,
      filterable: true,
      render: (value) => value || <span className="text-slate-400">-</span>
    },
    {
      key: 'created_display',
      label: 'Deschis',
      sortable: true,
      filterable: false,
      width: 'w-28',
      render: (value, row) => (
        <div className="text-xs">
          <p className="font-medium">{value}</p>
          <p className="text-slate-500">{row.created_time}</p>
        </div>
      )
    },
    {
      key: 'closed_display',
      label: 'Inchis',
      sortable: true,
      filterable: false,
      width: 'w-28',
      render: (value, row) => value ? (
        <div className="text-xs">
          <p className="font-medium">{value}</p>
          <p className="text-slate-500">{row.closed_time}</p>
        </div>
      ) : <span className="text-slate-400 text-xs">-</span>
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      filterable: false,
      width: 'w-16',
      render: (_, row) => (
        <div className="flex items-center" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/tickets/${row.id}`)} title="Deschide">
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-3 lg:space-y-5" data-testid="tickets-page">
      {/* Real-time new tickets banner */}
      {newTicketAlert && (
        <div
          className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={handleDismissNewBanner}
          data-testid="new-tickets-banner"
        >
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
            <span className="text-sm font-medium text-blue-800">
              {newTicketAlert.count === 1
                ? `1 tichet nou: "${newTicketAlert.lastTitle}"`
                : `${newTicketAlert.count} tichete noi primite`}
            </span>
          </div>
          <span className="text-xs text-blue-600 font-medium">Inchide</span>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900">Tichete Service</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Sesizari si cereri de interventie</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-11 sm:h-12" data-testid="add-ticket-btn">
              <Plus className="w-5 h-5 mr-2" />
              TICHET NOU
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Creaza Tichet Nou</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="h-11"
                  placeholder="Descriere scurta a problemei"
                  data-testid="ticket-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Nume si Prenume</Label>
                <Input
                  value={formData.reporter_name}
                  onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value.toUpperCase() })}
                  className="h-11"
                  placeholder="NUMELE SI PRENUMELE OPERATORULUI"
                  data-testid="ticket-reporter-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    value={formData.reporter_phone}
                    onChange={(e) => setFormData({ ...formData, reporter_phone: e.target.value })}
                    className="h-11"
                    placeholder="ex: 0722 123 456"
                    type="tel"
                    data-testid="ticket-reporter-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Departament / Sectie</Label>
                  <Input
                    value={formData.reporter_department}
                    onChange={(e) => setFormData({ ...formData, reporter_department: e.target.value.toUpperCase() })}
                    className="h-11"
                    placeholder="ex: CENTRU PRELUCRARE PVC"
                    data-testid="ticket-reporter-dept-input"
                  />
                </div>
              </div>

              {/* Equipment Combobox */}
              <div className="space-y-2">
                <Label>Echipament *</Label>
                <Combobox
                  items={equipmentItems}
                  value={formData.equipment_id}
                  onSelect={(id) => {
                    const eq = allEquipment.find(e => e.id === id);
                    setFormData(prev => ({
                      ...prev,
                      equipment_id: id || '',
                      plant_id: eq?.plant_id || '',
                      location_id: eq?.location_id || '',
                      priority_requested: eq?.ticket_priority || 'medium'
                    }));
                  }}
                  placeholder="Selecteaza echipamentul..."
                  searchPlaceholder="Cauta dupa nume sau cod..."
                  emptyText="Niciun echipament gasit."
                  codeKey="code"
                />
              </div>

              {formData.equipment_id && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Sediu (auto-completat)</Label>
                    <div className="h-11 flex items-center px-3 rounded-md border bg-slate-50 text-sm font-medium">
                      {selectedPlantName || 'N/A'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Locatie (auto-completata)</Label>
                    <div className="h-11 flex items-center px-3 rounded-md border bg-slate-50 text-sm font-medium">
                      {selectedLocationName || 'N/A'}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioritate solicitata *</Label>
                  <Select
                    value={formData.priority_requested}
                    onValueChange={(value) => setFormData({ ...formData, priority_requested: value })}
                  >
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critic</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">Ridicat</SelectItem>
                      <SelectItem value="medium">Mediu</SelectItem>
                      <SelectItem value="low">Scazut</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Simptom raportat</Label>
                  <Combobox
                    items={localSymptoms}
                    value={formData.symptom_id}
                    onSelect={(id) => setFormData({ ...formData, symptom_id: id })}
                    placeholder="Selecteaza simptomul..."
                    searchPlaceholder="Cauta simptom..."
                    emptyText="Niciun simptom gasit."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cauza identificata</Label>
                  <Combobox
                    items={localCauses}
                    value={formData.cause_id}
                    onSelect={(id) => setFormData({ ...formData, cause_id: id })}
                    placeholder="Selecteaza cauza..."
                    searchPlaceholder="Cauta cauza..."
                    emptyText="Niciun cauza gasita."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tip interventie</Label>
                  <Combobox
                    items={localInterventionTypes}
                    value={formData.intervention_type_id}
                    onSelect={(id) => setFormData({ ...formData, intervention_type_id: id })}
                    placeholder="Selecteaza tipul..."
                    searchPlaceholder="Cauta tip interventie..."
                    emptyText="Niciun tip gasit."
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="Descrieti problema in detaliu..."
                  data-testid="ticket-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Fotografii (optional, max 5)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageUpload} />
                  <Button type="button" variant="outline" className="h-11 gap-2" onClick={() => cameraInputRef.current?.click()}>
                    <Camera className="w-4 h-4" /> Fotografiaza
                  </Button>
                  <div className="relative">
                    <Input type="file" accept="image/*" multiple onChange={handleImageUpload} className="h-11 opacity-0 absolute inset-0 cursor-pointer" />
                    <Button type="button" variant="outline" className="h-11 w-full gap-2 pointer-events-none">
                      <ImageIcon className="w-4 h-4" /> Incarca
                    </Button>
                  </div>
                </div>
                {imagePreview.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {imagePreview.map((img, index) => (
                      <div key={index} className="relative group">
                        <img src={img.preview} alt={`Preview ${index}`} className="w-20 h-20 object-cover rounded-lg border" />
                        <button type="button" onClick={() => removeImage(index)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600">x</button>
                      </div>
                    ))}
                    <span className="text-xs text-slate-400 self-end">{imagePreview.length}/5</span>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Anuleaza</Button>
                <Button type="submit" className="btn-primary" data-testid="submit-ticket-btn" disabled={!formData.equipment_id}>CREAZA TICHET</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Smart Tabs + KPI */}
      <div className="space-y-3">
        {/* Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="ticket-tabs">
          {[
            { key: 'active',  label: 'Active',   count: pageData.tab_counts?.active ?? 0,  icon: AlertTriangle, color: 'text-red-600 border-red-500 bg-red-50' },
            { key: 'closed',  label: 'Rezolvate', count: pageData.tab_counts?.closed ?? 0,  icon: CheckCircle,   color: 'text-green-600 border-green-500 bg-green-50' },
            { key: 'all',     label: 'Toate',     count: pageData.tab_counts?.all    ?? 0,  icon: Ticket,        color: 'text-slate-600 border-slate-500 bg-slate-50' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? tab.color + ' shadow-sm'
                  : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-white/80' : 'bg-slate-100'
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* KPI Badges - only on Active tab */}
        {activeTab === 'active' && kpiCounts.total > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="kpi-badges">
            {kpiCounts.urgent > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                <AlertTriangle className="w-3 h-3" />{kpiCounts.urgent} Urgente
              </div>
            )}
            {kpiCounts.inProgress > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                <Play className="w-3 h-3" />{kpiCounts.inProgress} In Lucru
              </div>
            )}
            {kpiCounts.unassigned > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                <UserCog className="w-3 h-3" />{kpiCounts.unassigned} Neasignate
              </div>
            )}
            {kpiCounts.waitingParts > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                <Pause className="w-3 h-3" />{kpiCounts.waitingParts} Asteapta Piese
              </div>
            )}
          </div>
        )}
      </div>

      {/* Time Period Filter */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="time-period-filter">
        <CalendarDays className="w-4 h-4 text-slate-400 flex-shrink-0" />
        {[
          { key: 'today', label: 'Azi' },
          { key: 'yesterday', label: 'Ieri' },
          { key: 'this_week', label: 'Saptamana' },
          { key: 'this_month', label: 'Luna' },
          { key: 'last_month', label: 'Luna trecuta' },
          { key: 'all', label: 'Toate' },
        ].map(p => (
          <button
            key={p.key}
            onClick={() => handlePeriodChange(p.key)}
            data-testid={`time-filter-${p.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
              timePeriod === p.key
                ? 'bg-[#0052CC] text-white border-[#0052CC] shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {p.label}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
              timePeriod === p.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
            }`}>{pageData.period_counts?.[p.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Search + View Toggle */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cauta tichet..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10 h-10"
            data-testid="search-tickets-input"
          />
        </div>
        <Tabs value={activeView} onValueChange={setActiveView} className="hidden md:block">
          <TabsList className="h-10">
            <TabsTrigger value="table" className="px-3 h-8"><List className="w-4 h-4" /></TabsTrigger>
            <TabsTrigger value="cards" className="px-3 h-8"><Grid3X3 className="w-4 h-4" /></TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bulk Actions Bar */}
      {selectedTickets.length > 0 && hasRole('admin') && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-2" data-testid="bulk-actions-bar">
          <span className="text-sm text-red-700"><strong>{selectedTickets.length}</strong> tichete selectate</span>
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 border-red-300 hover:bg-red-100"
            onClick={async () => {
              if (!window.confirm(`Sigur doriti sa stergeti ${selectedTickets.length} tichete? Actiunea este ireversibila.`)) return;
              try {
                await api.post('/tickets/bulk-delete', selectedTickets);
                toast.success(`${selectedTickets.length} tichete sterse`);
                setSelectedTickets([]);
                fetchTickets();
              } catch (err) { toast.error('Eroare la stergere'); }
            }}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1.5" /> Sterge selectia
          </Button>
        </div>
      )}

      {/* Tickets List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : isMobile ? (
        /* Mobile Card View */
        <div className="space-y-2">
          {sortedTickets.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{activeTab === 'active' ? 'Niciun tichet activ' : activeTab === 'closed' ? 'Niciun tichet rezolvat' : 'Nu exista tichete'}</p>
            </div>
          ) : sortedTickets.map(ticket => (
            <MobileTicketCard
              key={ticket.id}
              ticket={ticket}
              onClick={() => navigate(`/tickets/${ticket.id}`)}
            />
          ))}
        </div>
      ) : activeView === 'table' ? (
        <DataTable
          data={ticketsData}
          columns={tableColumns}
          pageSize={15}
          searchable={false}
          exportable={true}
          printable={true}
          selectable={true}
          onSelectionChange={handleSelectionChange}
          onRowClick={(row) => navigate(`/tickets/${row.id}`)}
          emptyMessage={activeTab === 'active' ? 'Niciun tichet activ - totul e rezolvat!' : 'Nu exista tichete'}
        />
      ) : sortedTickets.length > 0 ? (
        <div className="grid gap-3">
          {sortedTickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="card-industrial hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              data-testid={`ticket-card-${ticket.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-slate-900">{ticket.title}</h3>
                      {ticket.images?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <ImageIcon className="w-3 h-3 mr-1" />{ticket.images.length}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-3">{ticket.description}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      {ticket.equipment_name && <span>{ticket.equipment_name}</span>}
                      <span>{new Date(ticket.created_at).toLocaleDateString('ro-RO')}</span>
                      {ticket.assigned_to_name && <span>{ticket.assigned_to_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(ticket.status)}>{getStatusLabel(ticket.status)}</Badge>
                    <Badge className={getPriorityColor(ticket.priority_assigned || ticket.priority_requested)}>
                      {getPriorityLabel(ticket.priority_assigned || ticket.priority_requested)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-industrial">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Ticket className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500">{activeTab === 'active' ? 'Niciun tichet activ!' : 'Nu exista tichete'}</p>
          </CardContent>
        </Card>
      )}

      {/* Pagination Controls */}
      {pageData.pages > 1 && (
        <div className="flex items-center justify-between pt-2" data-testid="pagination-controls">
          <p className="text-sm text-slate-500">
            {pageData.total} tichete &bull; Pagina {pageData.page} din {pageData.pages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="h-8 px-2 text-xs"
            >«</Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-8 px-3 text-xs"
            >Prev</Button>

            {/* Page number buttons — show up to 5 pages around current */}
            {Array.from({ length: pageData.pages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pageData.pages || Math.abs(p - page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i-1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) => p === '...'
                ? <span key={`ellipsis-${i}`} className="px-1 text-slate-400 text-xs">…</span>
                : <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                    onClick={() => setPage(p)}
                    className={`h-8 w-8 p-0 text-xs ${p === page ? 'bg-[#0052CC] text-white' : ''}`}
                  >{p}</Button>
              )
            }

            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.min(pageData.pages, p + 1))}
              disabled={page === pageData.pages}
              className="h-8 px-3 text-xs"
            >Next</Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(pageData.pages)}
              disabled={page === pageData.pages}
              className="h-8 px-2 text-xs"
            >»</Button>
          </div>
        </div>
      )}
      {pageData.pages === 1 && pageData.total > 0 && (
        <p className="text-xs text-slate-400 text-right pt-1">{pageData.total} tichete</p>
      )}
    </div>
  );
};

export default TicketsPage;
